<template>
  <div class="register-tab">
    <SalesRegisterToolbar
        ref="toolbarRef"
        :filters="appliedFilters"
        :chip-meta="filterChipMeta"
        :export-options="exportOptions"
        @go="onClickGo"
        @open-filter="onOpenFilter"
        @apply="onApplyFilters"
        @clear="onClearFilters"
        @remove-chip="onRemoveChip"
        @print="onClickToPrint"
        @export="onClickToExport"
    >
      <div class="col-12 col-sm-6">
        <label class="filter-label">Sales Person</label>
        <AsyncSelect
            placeholder="Select Sales Person"
            v-model="draftFilters.sales_person"
            :api-service="fetchAllSR"
            :option-selected="onChangeSalesPerson"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Referral</label>
        <AsyncSelect
            placeholder="Select Referrer"
            v-model="draftFilters.referral"
            :api-service="fetchContactProfiles"
            :additional-query="{ type: 'agent' }"
            :format-label="formatPatientLabel"
        />
      </div>
      <div v-if="canSearchCustomer" class="col-12 col-sm-6">
        <div class="d-flex align-items-center justify-content-between">
          <label class="filter-label">Customer</label>
          <span class="filter-search-icon" @click="onOpenContactSearchModal">
            <i class="fas fa-search"></i>
          </span>
        </div>
        <AsyncSelect
            placeholder="Select Customer"
            v-model="draftFilters.customer"
            :api-service="fetchContactProfiles"
            :additional-query="additionalContactOption"
            :format-label="formatPatientLabel"
            :key="contactRenderKey"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Order Type</label>
        <v-select
            placeholder="Select Order Type"
            v-model="draftFilters.order_type"
            :options="orderTypes"
            label="name"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Project</label>
        <v-select
            placeholder="Select Project"
            v-model="draftFilters.project"
            :options="projectList"
            label="name"
        />
      </div>
      <div class="col-12 col-sm-6">
        <label class="filter-label">Business Unit</label>
        <v-select
            placeholder="Select Business"
            v-model="draftFilters.business_unit"
            :options="businessList"
            label="name"
        />
      </div>
      <div class="col-12">
        <label class="filter-label">Bill Type</label>
        <v-select
            multiple
            placeholder="Select Bill Type"
            v-model="draftFilters.bill_type"
            :options="billTypeList"
            label="label"
        />
      </div>
    </SalesRegisterToolbar>
    <ListTable
        :tableItems="tableItems"
        :currentPage="invoiceData && invoiceData.data && invoiceData.data.current_page"
        :lastPage="invoiceData && invoiceData.data && invoiceData.data.last_page"
        :perPage="invoiceData && invoiceData.data && invoiceData.data.per_page"
        :totalValue="invoiceData && invoiceData.total"
        :isLoaded="hasLoaded"
    />
    <div class="mt-2">
      <Pagination :offset="offset" @onPageChange="onPageChange"/>
    </div>
    <ContactDropdownFilter
        v-if="canSearchCustomer && store.state.isModalOpenThree"
        @onSearchContact="onSearchContact"
        type="customer"
    />
    <Loader v-if="loading"/>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import Pagination from '@/components/atom/Pagination'
import Loader from '@/components/atom/LoaderComponent.vue'
import ContactDropdownFilter from '@/components/atom/Contact/ContactDropdownFilter.vue'
import ListTable from '@/components/molecule/company/inventory/product-sales-register/InvoiceTabTable.vue'
import SalesRegisterToolbar from '@/components/molecule/company/inventory/product-sales-register/SalesRegisterToolbar.vue'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handleCrm from '@/services/modules/crm/crm.js'
import handleContact from '@/services/modules/contact'
import handlePurchase from '@/services/modules/purchase'
import handleCompany from '@/services/modules/company'
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper'
import invoicePrinter from '@/services/utils/pdf/saleRegisterInvoice'

const props = defineProps({
  businessList: {
    type: Array,
    default: () => []
  },
  projectList: {
    type: Array,
    default: () => []
  },
  homeData: {
    type: Object,
    default: () => ({})
  }
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const showError = inject('showError')

const { fetchAllSR } = handleCrm()
const { fetchContactProfiles } = handleContact()
const { formatPatientLabel } = useAsyncDropdownHelper()
const { fetchSalesRegisterList, fetchSalesOrderTypes } = handlePurchase()
const { fetchCompanyInfo } = handleCompany()
const { exportToPDF } = invoicePrinter()

const loading = ref(false)
const hasLoaded = ref(false)
const tableItems = ref([])
const invoiceData = ref({})
const companyInfo = ref({})
const orderTypes = ref([])
const contactRenderKey = ref(0)
const toolbarRef = ref(null)
const offset = 10

const additionalContactOption = {
  with_mobile: 1,
  dealer_id: ''
}

const exportOptions = [
  { key: 'invoice', label: 'Invoice' },
  { key: 'output_tax', label: 'Output Tax Statement' }
]

const filterChipMeta = [
  { key: 'sales_person', label: 'Sales Person', text_key: 'name' },
  { key: 'referral', label: 'Referral', text_key: 'name' },
  { key: 'customer', label: 'Customer', text_key: 'name' },
  { key: 'order_type', label: 'Order Type', text_key: 'name' },
  { key: 'project', label: 'Project', text_key: 'name' },
  { key: 'business_unit', label: 'Business Unit', text_key: 'name' },
  { key: 'bill_type', label: 'Bill Type', text_key: 'label' }
]

const getEmptyFilters = () => {
  return {
    sales_person: null,
    referral: null,
    customer: null,
    order_type: null,
    project: null,
    business_unit: null,
    bill_type: []
  }
}

const cloneFilters = (filters) => {
  return Object.assign({}, filters, { bill_type: filters.bill_type.slice() })
}

const appliedFilters = ref(getEmptyFilters())
const draftFilters = ref(getEmptyFilters())

const start = computed(() => route.query.start)
const end = computed(() => route.query.end)
const canSearchCustomer = computed(() => ! route.query.contactId)

const billTypeList = computed(() => {
  const invoiceTypes = props.homeData.hospital_invoice_types
  if (! invoiceTypes) return []
  return Object.keys(invoiceTypes).map(value => {
    return { value: value, label: invoiceTypes[value] }
  })
})

const getCustomerId = () => {
  if (route.query.contactId) return route.query.contactId
  if (! appliedFilters.value.customer) return ''
  return appliedFilters.value.customer.id
}

const getQuery = () => {
  const filters = appliedFilters.value
  const customerId = getCustomerId()
  let query = `?company_id=${route.params.companyId}`
  if (start.value && end.value) query += `&start_date=${start.value}&end_date=${end.value}`
  if (route.query.page) query += '&page=' + route.query.page
  if (filters.sales_person) query += `&sale_representative_id=${filters.sales_person.id}`
  if (filters.referral) query += `&referral_id=${filters.referral.id}`
  if (customerId) query += `&contact_profile_id=${customerId}`
  if (filters.order_type) query += `&sale_type=${filters.order_type.value}`
  if (filters.business_unit) query += '&business_unit=' + filters.business_unit.id
  if (filters.project) query += '&project_id=' + filters.project.id
  if (filters.bill_type.length) query += '&bill_type=' + filters.bill_type.map(option => option.value).join(',')
  query += '&offset=' + offset
  return query
}

const getPrintParams = () => {
  const filters = appliedFilters.value
  return {
    contact_profile: !! getCustomerId(),
    sale_representative: !! filters.sales_person,
    project: !! filters.project,
    sale_type: !! filters.order_type,
    business_unit: !! filters.business_unit
  }
}

const setPagination = (data) => {
  store.commit('setPaginationData', {
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page
  })
}

const resetTable = () => {
  tableItems.value = []
  invoiceData.value = {}
  store.commit('resetPagination')
}

const getSaleRegisterList = async () => {
  loading.value = true
  try {
    const res = await fetchSalesRegisterList(getQuery())
    if (! res.status) {
      resetTable()
      return
    }
    tableItems.value = res.data.data
    invoiceData.value = res
    setPagination(res.data)
  } catch (err) {
    showError('Something went wrong')
  } finally {
    hasLoaded.value = true
    loading.value = false
  }
}

const resetPage = async () => {
  store.commit('resetPagination')
  await router.push({ path: route.path, query: Object.assign({}, route.query, { page: 1 }) })
}

const onClickGo = async () => {
  await resetPage()
  await getSaleRegisterList()
}

const onPageChange = async (page) => {
  const routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({ path: route.path, query: routeQuery })
  await getSaleRegisterList()
}

const onOpenFilter = () => {
  draftFilters.value = cloneFilters(appliedFilters.value)
}

const onApplyFilters = async () => {
  appliedFilters.value = cloneFilters(draftFilters.value)
  await onClickGo()
}

const onClearFilters = async () => {
  appliedFilters.value = getEmptyFilters()
  draftFilters.value = getEmptyFilters()
  await onClickGo()
}

const onRemoveChip = async (key) => {
  appliedFilters.value[key] = getEmptyFilters()[key]
  await onClickGo()
}

const onChangeSalesPerson = () => {
  draftFilters.value.customer = null
  contactRenderKey.value += 1
}

const onOpenContactSearchModal = () => {
  store.state.isModalOpenThree = true
}

const onSearchContact = (contact) => {
  const draft = Object.assign({}, draftFilters.value, { customer: contact })
  if (toolbarRef.value) toolbarRef.value.openFilter()
  draftFilters.value = draft
}

const onClickToPrint = async () => {
  loading.value = true
  try {
    const res = await fetchSalesRegisterList(getQuery() + '&is_all=true')
    if (! res.status) {
      showError('No data found')
      return
    }
    exportToPDF(companyInfo.value, res, start.value || '', end.value || '', getPrintParams())
  } catch (err) {
    showError('Something went wrong')
  } finally {
    loading.value = false
  }
}

const downloadFile = (url) => {
  const anchor = document.createElement('a')
  anchor.setAttribute('href', url)
  anchor.click()
}

const exportInvoice = () => {
  const token = localStorage.getItem('token')
  downloadFile(`${process.env.VUE_APP_BASE_URL}/export/inventory/sales-register${getQuery()}&_token=${token}&is_export=1&is_invoice=1`)
}

const exportOutputTax = () => {
  if (! start.value || ! end.value) {
    showError('Please select a date range')
    return
  }
  const token = localStorage.getItem('token')
  downloadFile(`${process.env.VUE_APP_BASE_URL}/export/inventory/output-tax-statement${getQuery()}&_token=${token}`)
}

const exportHandlers = {
  invoice: exportInvoice,
  output_tax: exportOutputTax
}

const onClickToExport = (type) => {
  if (! exportHandlers[type]) return
  exportHandlers[type]()
}

const getSaleOrderTypes = async () => {
  const res = await fetchSalesOrderTypes(`?company_id=${route.params.companyId}`)
  if (! res.status) return
  orderTypes.value = res.data
}

const setCompanyInfo = async () => {
  try {
    const res = await fetchCompanyInfo(route.params.companyId)
    companyInfo.value = res.data
  } catch (err) {
    showError('Failed to load company info')
  }
}

onMounted(() => {
  getSaleOrderTypes()
  setCompanyInfo()
})
</script>
